import { StableMessageListPanel } from "./StableMessageListPanel";
import { ActiveChannelReadReceiptsProvider } from "./ActiveChannelReadReceiptsProvider";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import {
  API_get_chat_users,
  API_create_chat_channel,
  API_promote_channel_moderator,
  API_demote_channel_moderator,
  API_delete_channel_message,
  API_add_direct_message_members,
  API_leave_chat_channel,
  API_delete_chat_channel,
  API_remove_chat_channel_member,
} from "../../../constants";
import {
  Channel,
  ChannelList,
  Window,
  WithComponents,
  ChannelHeader,
  MessageList,
  MessageInput,
  MessageSimple,
  Thread,
  useTranslationContext,
  useChatContext,
  useChannelActionContext,
  useChannelStateContext,
  useMessageContext,
  MESSAGE_ACTIONS,
} from "stream-chat-react";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import ThreadSummaryButton from "./ThreadSummaryButton";
import "stream-chat-react/dist/css/v2/index.css";
import "./ChatStyling2.css";
import CreateChannelModal from "./CreateChannelModal";
import ConversationDetailsModal from "./ConversationDetailsModal";
import EditIcon from "@mui/icons-material/Edit";
import LoadSpinner from "../../../utilities/LoadSpinner";
import AddIcon from "@mui/icons-material/Add";
import CampaignIcon from '@mui/icons-material/Campaign';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HandymanIcon from '@mui/icons-material/Handyman';
import GroupsIcon from '@mui/icons-material/Groups';
import { formatName } from "../../../helpers/helpers";
import {
  filterChatUsers,
  getDirectMessageUserId,
  mergeChatSearchResults,
} from "./chatSearchUtils";
import {
  MessageStatusWithViewers,
  MessageTimestampWithViewers,
  ReadReceiptsPolicyContext,
} from "./MessageReadReceipts";
import { EmojiPicker } from "stream-chat-react/emojis";
import FullReactionSelector, {
  ReactionOptionsHistorySync,
  useChannelReactionOptions,
} from "./FullReactionSelector";
import ChatSidebarSections from "./ChatSidebarSections";
import AutoLoadPaginator from "./AutoLoadPaginator";
import NewMessageComposer from "./NewMessageComposer";
import ScheduleSendButton from "./ScheduleSendButton";
import ScheduledMessagesBanner from "./ScheduledMessagesBanner";
import ScheduledMessagesPanel from "./ScheduledMessagesPanel";
import {
  ScheduledMessagesProvider,
  scheduledMessageErrorText,
  useScheduledMessagesState,
} from "./ScheduledMessagesContext";
import { formatScheduledTime } from "./scheduledMessageUtils";
import {
  getChannelDisplayName,
  getDirectMessageOtherMembers,
  makeTodayDateResolver,
  makeStoreNameMap,
  getPermissionRoleForChannel,
  isAutomatedChannel,
  isGroupDirectMessage,
  isSupportChannel,
  isTicketChannel,
} from "./chatSidebarSectionUtils";

const isClientConnected = (chatClient) => !!chatClient?.userID;
const messageListComponents = { MessageListMainPanel: StableMessageListPanel };
const SUPPORT_CHANNEL_STORAGE_KEY = "onboarding-support-channel-id";

const automatedBadgeClassName =
  "inline-flex shrink-0 items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xxs font-medium text-emerald-700";
const ADMIN_PERMISSION_TYPES = ["company_admin", "rightwork_internal"];
const READ_RECEIPT_VIEWER_ROLES = [
  "company_manager",
  "company_admin",
  "rightwork_internal",
];
const MODERATED_MESSAGE_ACTIONS = Object.values(MESSAGE_ACTIONS)
  .filter((action) => action !== MESSAGE_ACTIONS.delete);

const AutomatedBadge = () => (
  <div className={automatedBadgeClassName}>
    Automated
  </div>
);

const AutomatedChannelIcon = ({ size = 22 }) => (
  <GroupsIcon style={{ fontSize: `${size}px` }} />
);

// "?" rendered like the "#" channel glyph, so support reads the same on
// web and mobile
const QuestionGlyph = ({ style }) => (
  <span
    className="font-semibold leading-none"
    style={{ fontSize: style?.fontSize || "20px" }}
  >
    ?
  </span>
);

// Team channels with their own avatar treatment (instead of # / automated):
// ticket channels are amber with a wrench, Rightwork support is violet with
// a support-agent icon so it stands apart from the company's own channels
// Stream enforces who may post (announcement channels are read-only for
// everyone but moderators) and reports the answer as own_capabilities. Without
// this the composer looks usable and the send just fails.
const canSendInChannel = (channel) => {
  const capabilities = channel?.data?.own_capabilities;
  // Absent capabilities means they simply haven't loaded; don't hide the
  // composer on a channel the user can actually post in.
  if (!Array.isArray(capabilities)) return true;
  return capabilities.includes("send-message");
};

// Why the composer is missing, in the channel's own terms.
const readOnlyNoticeText = (channel) =>
  channel?.type === "announcement"
    ? "Only admins can post announcements."
    : "You can't send messages in this channel.";

// Thread renders a composer of its own whatever the main one does, and takes
// only a component to render in its place, so the notice has to be one.
const ReadOnlyComposerNotice = () => {
  const { channel } = useChannelStateContext("ReadOnlyComposerNotice");
  return (
    <p className="px-4 py-3 text-center text-sm text-slate-500">
      {readOnlyNoticeText(channel)}
    </p>
  );
};

const getSpecialTeamChannelStyle = (channel) => {
  if (isTicketChannel(channel)) {
    return { bg: "bg-amber-500", Icon: HandymanIcon };
  }
  if (isSupportChannel(channel)) {
    return { bg: "bg-violet-700", Icon: QuestionGlyph };
  }
  return null;
};

// Overlapping initial bubbles for group direct messages: up to three along
// a diagonal; with more than three other members the third bubble is "+N"
const groupAvatarCells = (members) => {
  const initials = members.map(
    (member) => member?.user?.name?.trim()?.[0]?.toUpperCase() || "?"
  );
  if (initials.length <= 3) return initials;
  return [...initials.slice(0, 2), `+${initials.length - 2}`];
};

const GROUP_AVATAR_BUBBLE_CLASSES = ["bg-violet-700", "bg-violet-500", "bg-violet-400"];

// Bubble centers as fractions of the slot: two on a diagonal, three in a
// triangle (one up top, two below)
const groupAvatarPositions = (count) =>
  count >= 3
    ? [
        { x: 0.5, y: 0.3 },
        { x: 0.28, y: 0.7 },
        { x: 0.72, y: 0.7 },
      ]
    : [
        { x: 0.34, y: 0.34 },
        { x: 0.66, y: 0.66 },
      ];

const GroupDirectMessageAvatar = ({ members = [], size = 40 }) => {
  const cells = groupAvatarCells(members);
  const bubble = Math.round(size * (cells.length >= 3 ? 0.6 : 0.68));
  const positions = groupAvatarPositions(cells.length);
  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size, isolation: "isolate", zIndex: 0 }}
    >
      {cells.map((label, index) => {
        const isOverflow = label.startsWith("+");
        const { x, y } = positions[index] || positions[0];
        return (
          <div
            key={index}
            className={`absolute flex items-center justify-center rounded-full border-2 border-white text-white ${
              isOverflow ? "bg-slate-400" : GROUP_AVATAR_BUBBLE_CLASSES[index]
            }`}
            style={{
              width: bubble,
              height: bubble,
              fontSize: Math.round(bubble * (isOverflow ? 0.4 : 0.5)),
              lineHeight: 1,
              top: Math.round(size * y - bubble / 2),
              left: Math.round(size * x - bubble / 2),
              zIndex: index + 1,
            }}
          >
            {label}
          </div>
        );
      })}
    </div>
  );
};

const normalizePreviewText = (text) => {
  if (typeof text !== "string") {
    return text;
  }

  return text.replace(/\s+/g, " ").trim();
};

const ChannelNameWithBadge = ({ title, automated, titleClassName = "" }) => (
  <div className="flex min-w-0 max-w-full flex-wrap items-center gap-x-2 gap-y-1 overflow-hidden">
    <p className={`min-w-0 max-w-full break-words ${titleClassName}`.trim()}>
      {title}
    </p>
    {automated && <AutomatedBadge />}
  </div>
);

// "You: " / "Sam: " in front of the preview for conversations with more than
// two people (Slack/WhatsApp); 1:1 DMs don't need it
const getPreviewSenderPrefix = (channel, lastMessage, currentUserId) => {
  const sender = lastMessage?.user;
  if (!sender?.id || lastMessage?.type === "deleted") return "";
  const memberCount =
    channel?.data?.member_count ??
    Object.keys(channel?.state?.members || {}).length;
  if (channel?.type === "messaging" && memberCount <= 2) return "";
  if (sender.id?.toString() === currentUserId?.toString()) return "You: ";
  const firstName = (sender.name || "").trim().split(" ")[0];
  return firstName ? `${firstName}: ` : "";
};

const ChannelPreviewText = ({
  title,
  latestMessage,
  latestMessageAt,
  timestamp,
  unreadMessageCount,
  senderPrefix = "",
}) => {
  const hasUnread = unreadMessageCount > 0;
  return (
    <div className="min-w-0 overflow-hidden">
      <div className="flex min-w-0 items-start justify-between gap-x-3 overflow-hidden">
        <div className="flex min-w-0 flex-1 items-start gap-x-2 overflow-hidden">
          <p title={title} className={`min-w-0 flex-1 truncate ${hasUnread ? "font-semibold text-slate-900" : ""}`}>
            {title}
          </p>
          {hasUnread && (
            <div className="mt-1 flex h-3.5 min-w-[14px] shrink-0 items-center justify-center rounded-full bg-rose-600 px-1 text-xxs text-white">
              {unreadMessageCount}
            </div>
          )}
        </div>
        <time
          dateTime={latestMessageAt?.toISOString?.()}
          className="shrink-0 pt-0.5 text-xs text-slate-500"
        >
          {timestamp}
        </time>
      </div>
      <div
        className={`rw-chat-preview-latest mt-0.5 min-w-0 truncate text-sm ${
          hasUnread ? "text-slate-700" : "text-slate-500"
        }`}
      >
        {senderPrefix}
        {normalizePreviewText(latestMessage)}
      </div>
    </div>
  );
};

const buildDeletedMessage = (message, responseMessage) => (
  responseMessage || {
    ...message,
    deleted_at: new Date().toISOString(),
    text: "",
    type: "deleted",
  }
);

// Click-to-reveal timestamps (parity with mobile): clicking a bubble reveals
// the times for its whole consecutive-same-sender group. Stream renders the
// metadata row (with the timestamp) on every message and hides it via CSS on
// grouped rows, so revealing is just a class that un-hides it (see
// ChatStyling2.css). State travels via context so the module-scope message UI
// component stays stable.
const RevealedTimestampsContext = React.createContext({
  revealedTimestampIds: new Set(),
  toggleGroupTimestamps: () => {},
});

const collectGroupTimestampIds = (targetMessage, channel) => {
  const sourceMessages = targetMessage?.parent_id
    ? channel?.state?.threads?.[targetMessage.parent_id] || []
    : channel?.state?.messages || [];
  const index = sourceMessages.findIndex(
    (item) => item?.id === targetMessage?.id
  );
  if (index === -1) return [targetMessage.id];
  const senderId = sourceMessages[index]?.user?.id;
  // Mirror Stream's visual grouping: consecutive messages from the same
  // sender, not split by a system/error/deleted bubble or a date separator
  const isGroupable = (candidate, neighbor) =>
    candidate?.user?.id === senderId &&
    !["system", "error", "deleted"].includes(candidate?.type) &&
    !candidate?.deleted_at &&
    new Date(candidate?.created_at).toDateString() ===
      new Date(neighbor?.created_at).toDateString();
  const ids = [sourceMessages[index].id];
  for (
    let i = index - 1;
    i >= 0 && isGroupable(sourceMessages[i], sourceMessages[i + 1]);
    i -= 1
  ) {
    ids.push(sourceMessages[i].id);
  }
  for (
    let i = index + 1;
    i < sourceMessages.length &&
    isGroupable(sourceMessages[i], sourceMessages[i - 1]);
    i += 1
  ) {
    ids.push(sourceMessages[i].id);
  }
  return ids;
};

const MessageSimpleWithRevealableTimestamps = (props) => {
  const { groupStyles, message } = useMessageContext(
    "MessageSimpleWithRevealableTimestamps"
  );
  const { channel } = useChannelStateContext(
    "MessageSimpleWithRevealableTimestamps"
  );
  const { revealedTimestampIds, toggleGroupTimestamps } = React.useContext(
    RevealedTimestampsContext
  );

  const isDeleted = !!message?.deleted_at || message?.type === "deleted";
  const groupStyle = groupStyles?.[0];
  // Stream's CSS only hides the metadata row on top/middle rows of a group;
  // bottom/single rows already show their time
  const metadataHiddenByGrouping =
    groupStyle === "top" || groupStyle === "middle";
  const revealed =
    metadataHiddenByGrouping &&
    !isDeleted &&
    revealedTimestampIds.has(message?.id);

  const handleClick = (event) => {
    if (isDeleted || !message?.id) return;
    // Leave interactive content alone, and don't toggle when the user is
    // selecting message text to copy
    if (event.target.closest("a, img, video, audio, button, input, textarea"))
      return;
    if (window.getSelection?.()?.toString()) return;
    toggleGroupTimestamps(message, channel);
  };

  return (
    <div
      className={revealed ? "rw-timestamp-revealed" : undefined}
      onClick={handleClick}
    >
      <MessageSimple {...props} />
    </div>
  );
};

// Message timestamps: time for today, "Yesterday" + time, then date + time
// (Stream's default calendar format drops the time on older messages)
const formatMessageTimestamp = (date) => {
  if (!date || isNaN(date.getTime())) return "";
  const time = date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return time;
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday ${time}`;
  }
  const day = date.toLocaleDateString([], {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
  return `${day} ${time}`;
};

function ChatDisplay(props) {
  const [channel, setChannel] = useState(null);
  const [userID, setUserID] = useState(null);
  const [revealedTimestampIds, setRevealedTimestampIds] = useState(
    () => new Set()
  );

  useEffect(() => {
    setRevealedTimestampIds(new Set());
  }, [channel]);

  const toggleGroupTimestamps = React.useCallback((message, activeChannel) => {
    if (!message?.id) return;
    setRevealedTimestampIds((previous) =>
      previous.has(message.id)
        ? new Set()
        : new Set(collectGroupTimestampIds(message, activeChannel))
    );
  }, []);

  const revealedTimestampsContextValue = React.useMemo(
    () => ({ revealedTimestampIds, toggleGroupTimestamps }),
    [revealedTimestampIds, toggleGroupTimestamps]
  );
  // Managers and up can see who has read messages they didn't send, judged
  // by their role at the channel's own store (not the store they have open)
  const readReceiptsPolicy = React.useMemo(
    () => ({
      canSeeOthersReceipts: (channel) =>
        READ_RECEIPT_VIEWER_ROLES.includes(
          getPermissionRoleForChannel(channel, props.storeOptions, props.permissionRole)
        ),
    }),
    [props.permissionRole, props.storeOptions]
  );
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  // "New message" pane: pick people, first send creates the conversation
  const [composing, setComposing] = useState(false);
  // Conversation details sheet: the channel it describes (null = closed)
  const [detailsChannel, setDetailsChannel] = useState(null);
  const [modalType, setModalType] = useState("Channel");
  const [modalEdit, setModalEdit] = useState(false);
  // For the create-channel modal
  const [channelName, setChannelName] = useState("");
  const [channelDescription, setChannelDescription] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const searchRequestIdRef = React.useRef(0);


  // For tooltip
  const [showTooltip, setShowTooltip] = useState(false);

  // Chat users
  const [chatUsers, setChatUsers] = React.useState([]);

  const { setActiveChannel, client } = useChatContext();

  // Scheduled messages: the list panel takes over the chat window the way
  // the "New message" pane does, and closes as soon as a channel opens.
  const [showScheduled, setShowScheduled] = useState(false);
  const openScheduledList = React.useCallback(() => {
    setChannel(null);
    setActiveChannel?.(null);
    setComposing(false);
    setShowScheduled(true);
  }, [setActiveChannel]);
  const scheduledMessagesState = useScheduledMessagesState({
    storeId: props.homeStore,
    client,
    onOpenList: openScheduledList,
    listOpen: showScheduled,
  });
  useEffect(() => {
    if (channel || composing) setShowScheduled(false);
  }, [channel, composing]);
  // Moves on every change of conversation or store, so a scheduled-message
  // open that finishes after the user went elsewhere is dropped.
  const navigationToken = React.useRef(0);
  useEffect(() => {
    navigationToken.current += 1;
  }, [channel, props.homeStore]);

  // Stream reports what this user may do in a channel as
  // channel.data.own_capabilities, and a promotion or demotion rewrites it in
  // place on the channel object held in state here, so nothing re-renders:
  // the composer stayed hidden for a moderator who had just been promoted,
  // and stayed available to a demoted one whose sends the server then
  // rejected. Bump on the events that can move it.
  const [, setCapabilityRevision] = useState(0);
  useEffect(() => {
    if (!channel) return undefined;
    const bump = () => setCapabilityRevision((value) => value + 1);
    const subscriptions = [
      // Raised by the SDK itself once a query or a partial update finds the
      // capability set has changed, and it carries a cid, so it reaches
      // channel listeners.
      channel.on("capabilities.changed", bump),
      channel.on("channel.updated", bump),
      // A role change arrives as member.updated, which does NOT recompute
      // own_capabilities on its own. Re-query when it's this user who moved so
      // the SDK refreshes them (and raises capabilities.changed if they did
      // move). Same shape the SDK uses for a frozen-flag change.
      channel.on("member.updated", (event) => {
        const changedId = event?.member?.user_id || event?.member?.user?.id;
        if (!changedId || changedId !== client?.userID) return;
        // state: true, not false. _initializeState assigns
        // this.state.membership = state.membership || {} unconditionally, and
        // a state-less response carries no membership, so the cheaper query
        // would drop membership.pinned_at and silently unpin the conversation
        // in the sidebar, the header, and the details sheet. The message and
        // watcher limits keep it to a channel-data refresh either way.
        channel
          .query({ state: true, messages: { limit: 0 }, watchers: { limit: 0 } })
          .catch(() => {});
      }),
    ];
    return () => {
      subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
    };
  }, [channel, client?.userID]);
  // Quick reactions + any emoji already used in this channel
  const { reactionOptions, refreshReactionOptions } = useChannelReactionOptions(channel);
  // Daily channels are labelled "Working today/tomorrow" against their own
  // store's calendar day (users can span stores in different timezones)
  const dailyStoreNames = React.useMemo(
    () => makeStoreNameMap(props.storeNames, props.homeCompany),
    [props.storeNames, props.homeCompany]
  );
  const todayDateStr = React.useMemo(
    () => makeTodayDateResolver(props.storeTimezones, props.homeStoreTimezone),
    [props.storeTimezones, props.homeStoreTimezone]
  );


  const fetchChatUsers = async () => {
    let api_params = { store_id: props.homeStore };
    let api = API_get_chat_users;
    try {
      const res = await getRightworkAPI(api, api_params);
      // console.log(res);
      if (res.status === 200) {
        const data = res.data?.redux?.payload;
        setChatUsers(data);
        // console.log("CHAT USERS", data);
      }
    } catch (error) {
      throw Error(error);
    }
  };

  useEffect(() => {
    if (client && client.userID) {
      setUserID(client.userID);
    }
    fetchChatUsers();
  }, [client, props.homeStore]);

  useEffect(() => {
    const supportChannelId =
      typeof window !== "undefined"
        ? window.sessionStorage.getItem(SUPPORT_CHANNEL_STORAGE_KEY)
        : null;

    if (!supportChannelId || !isClientConnected(client)) {
      return;
    }

    let cancelled = false;

    const openSupportChannel = async () => {
      try {
        const supportChannel = client.channel("team", supportChannelId);
        await supportChannel.watch();
        if (cancelled) {
          return;
        }
        setActiveChannel?.(supportChannel);
        setChannel(supportChannel);
      } catch (error) {
        console.error("Error opening onboarding support channel:", error);
      } finally {
        window.sessionStorage.removeItem(SUPPORT_CHANNEL_STORAGE_KEY);
      }
    };

    openSupportChannel();

    return () => {
      cancelled = true;
    };
  }, [client, setActiveChannel]);

  // Reset local channel state when switching stores
  useEffect(() => {
    setChannel(null);
    setActiveChannel?.(null);
    setComposing(false);
    setShowScheduled(false);
  }, [props.homeStore]);

  // Clear channel when the client disconnects
  useEffect(() => {
    if (!isClientConnected(client)) {
      setChannel(null);
      setActiveChannel?.(null);
    }
  }, [client?.userID]);


  // console.log("CHAT USERS CHAT DISPLAY", chatUsers);

  const filters = { members: { $in: [userID] } };
  const options = { limit: 30, presence: true, state: true };
  // pinned_at must come first so pinned conversations stay at the top
  const sort = [{ pinned_at: -1 }, { last_message_at: -1 }];
  const searchableChannelTypes = ["team", "daily", "announcement", "messaging"];

  const getChannelSearchTitle = (searchChannel) =>
    getChannelDisplayName(searchChannel, userID, todayDateStr, dailyStoreNames);

  const getChannelNameSearchMatches = (searchText) => {
    const normalizedSearchText = searchText.toLowerCase();
    const activeChannels = Object.values(client?.activeChannels || {});

    return activeChannels.filter((activeChannel) => {
      const channelName = getChannelSearchTitle(activeChannel);

      return (
        searchableChannelTypes.includes(activeChannel?.type) &&
        channelName.toLowerCase().includes(normalizedSearchText)
      );
    });
  };

  const searchChannelsAndUsers = async (params, event) => {
    const { setQuery, setResults, setSearching } = params;
    const searchRequestId = searchRequestIdRef.current + 1;
    const value = event.target.value || "";
    const searchText = value.trim();
    const isCurrentSearchRequest = () => searchRequestIdRef.current === searchRequestId;

    searchRequestIdRef.current = searchRequestId;

    setQuery(value);

    if (!searchText || !isClientConnected(client)) {
      setResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    const localChannelResults = getChannelNameSearchMatches(searchText);
    const userResults = filterChatUsers(chatUsers, searchText).slice(0, 10);
    if (isCurrentSearchRequest()) {
      setResults(
        mergeChatSearchResults(userID, localChannelResults, userResults)
      );
    }

    try {
      const channelSearches = searchableChannelTypes.map((channelType) =>
        client.queryChannels(
          {
            members: { $in: [userID] },
            name: { $autocomplete: searchText },
            type: channelType,
          },
          { last_message_at: -1, updated_at: -1 },
          { limit: 10, presence: true, state: true },
        )
      );

      const channelSearchResults = await Promise.allSettled(channelSearches);
      const remoteChannelResults = channelSearchResults.flatMap((result) => {
        if (result.status === "fulfilled") {
          return result.value;
        }

        console.error("Error searching chat channels:", result.reason);
        return [];
      });

      const searchableUserIds = chatUsers.map((user) => user.id.toString());
      let remoteUserResults = [];

      if (searchableUserIds.length > 0) {
        try {
          const response = await client.queryUsers(
            {
              $or: [
                { id: { $autocomplete: searchText } },
                { name: { $autocomplete: searchText } },
              ],
              id: { $in: searchableUserIds },
            },
            { name: 1 },
            { limit: 10, presence: true },
          );
          remoteUserResults = (response.users || []).map((streamUser) => ({
            ...streamUser,
            email: chatUsers.find(
              (chatUser) =>
                chatUser.id?.toString() === streamUser.id?.toString()
            )?.email,
          }));
        } catch (error) {
          console.error("Error searching chat users:", error);
        }
      }

      if (!isCurrentSearchRequest()) {
        return;
      }

      setResults(
        mergeChatSearchResults(
          userID,
          localChannelResults,
          remoteChannelResults,
          remoteUserResults,
          userResults,
        )
      );
    } catch (error) {
      console.error("Error searching chat channels:", error);
      if (isCurrentSearchRequest()) {
        setResults(
          mergeChatSearchResults(userID, localChannelResults, userResults)
        );
      }
    } finally {
      if (isCurrentSearchRequest()) {
        setSearching(false);
      }
    }
  };

  const handleCreateChannel = async (channelName, selectedMembers, channelDescription) => {
    if (!isClientConnected(client)) return;
    const stringMembers = selectedMembers.map((member) => member.id.toString());
    const newChannel = client.channel("team", window.crypto.randomUUID(), {
      name: channelName,
      description: channelDescription || "",
      members: [userID.toString(), ...stringMembers],
    });
    try {
      const response = await postRightworkAPI(API_create_chat_channel, {
        channel_type: "team",
        member_ids: stringMembers,
        name: channelName,
        store_id: props.homeStore,
      });
      const channelID = response?.data?.redux?.payload?.channel_id;
      if (!channelID) throw new Error("Chat channel was not created");
      const newChannel = client.channel("team", channelID);
      await newChannel.watch();

      // Auto-promote managers and above to moderator
      const moderatorRoles = ["company_manager", "company_admin", "rightwork_internal"];
      const membersToPromote = selectedMembers.filter(
        (member) => member?.permission_type && moderatorRoles.includes(member.permission_type)
      );
      for (const member of membersToPromote) {
        try {
          await postRightworkAPI(API_promote_channel_moderator, {
            channel_id: newChannel.id,
            user_id: member.id.toString(),
            store_id: props.homeStore,
          });
        } catch (err) {
          console.error("Error auto-promoting member:", err);
        }
      }

      if (membersToPromote.length > 0) {
        await newChannel.query();
      }

      setActiveChannel?.(newChannel);
      setChannel(newChannel);
      setShowCreateChannel(false);
      setComposing(false);
    } catch (error) {
      console.error("Error creating channel:", error);
      toast.error(
        error?.response?.data?.system?.message ||
          "We couldn't create this channel. Please try again.",
        { autoClose: 3000 }
      );
    }
  };

  const handleCreateDM = async (selectedMember) => {
    if (!isClientConnected(client)) return;
    try {
      const response = await postRightworkAPI(API_create_chat_channel, {
        channel_type: "messaging",
        member_ids: [selectedMember.id.toString()],
        store_id: props.homeStore,
      });
      const channelID = response?.data?.redux?.payload?.channel_id;
      if (!channelID) throw new Error("Direct message channel was not created");
      const newChannel = client.channel("messaging", channelID);
      await newChannel.watch();
      setActiveChannel?.(newChannel);
      setChannel(newChannel);
      setShowCreateChannel(false);
      setComposing(false);
    } catch (error) {
      console.error("Error creating channel:", error);
      toast.error(
        error?.response?.data?.system?.message ||
          "We couldn't open this conversation. Please try again.",
        { autoClose: 3000 }
      );
    }
  };

  // Deep link from the schedule ("Message <name>"): open the DM once Stream
  // is connected, then clear so a later visit doesn't reopen it.
  useEffect(() => {
    const target = props.pendingChatTarget;
    if (!target?.userId || !isClientConnected(client)) return;
    // The DM was created for the store selected at click time; if the
    // manager has switched stores since, drop it rather than open it here.
    if (target.storeId != null && target.storeId !== props.homeStore) {
      props.clearPendingChatTarget?.();
      return;
    }
    let cancelled = false;
    const open = async () => {
      const channelId = target.channelIdPromise
        ? await target.channelIdPromise
        : null;
      if (cancelled) return;
      if (channelId) {
        try {
          const dm = client.channel("messaging", channelId);
          await dm.watch();
          if (cancelled) return;
          setActiveChannel?.(dm);
          setChannel(dm);
          setComposing(false);
          // Clear last: clearing re-runs this effect's cleanup, which would
          // otherwise cancel the open while the watch is still in flight.
          props.clearPendingChatTarget?.();
          return;
        } catch (error) {
          // fall through to the full create path below
        }
      }
      // Fallback: create / reuse the channel here, under the same guard, so
      // a store switch mid-request can't activate the old store's DM.
      try {
        const response = await postRightworkAPI(API_create_chat_channel, {
          channel_type: "messaging",
          member_ids: [target.userId.toString()],
          store_id: target.storeId ?? props.homeStore,
        });
        if (cancelled) return;
        const createdId = response?.data?.redux?.payload?.channel_id;
        if (!createdId) throw new Error("Direct message channel was not created");
        const dm = client.channel("messaging", createdId);
        await dm.watch();
        if (cancelled) return;
        setActiveChannel?.(dm);
        setChannel(dm);
        setComposing(false);
      } catch (error) {
        if (cancelled) return;
        toast.error(
          error?.response?.data?.system?.message ||
            "We couldn't open this conversation. Please try again.",
          { autoClose: 3000 }
        );
      } finally {
        if (!cancelled) props.clearPendingChatTarget?.();
      }
    };
    open();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.pendingChatTarget, client, props.homeStore]);

  // Leaving Chat while a deep-linked DM is still opening abandons it; don't
  // let it reopen on the next visit. Unmount-only (empty deps), so effect
  // restarts above can still retry.
  const clearPendingChatTargetRef = React.useRef(props.clearPendingChatTarget);
  clearPendingChatTargetRef.current = props.clearPendingChatTarget;
  useEffect(() => {
    return () => {
      clearPendingChatTargetRef.current?.();
    };
  }, []);

  // First send from the "New message" pane: create (or reuse) the 1:1 / group
  // conversation server-side, then send the text into it. Errors propagate
  // so the composer can show them inline.
  const handleStartConversation = async (recipients, text) => {
    if (!isClientConnected(client)) {
      throw new Error("Chat is not connected");
    }
    const response = await postRightworkAPI(API_create_chat_channel, {
      channel_type: "messaging",
      member_ids: recipients.map((recipient) => recipient.id.toString()),
      store_id: props.homeStore,
    });
    const channelID = response?.data?.redux?.payload?.channel_id;
    if (!channelID) throw new Error("Conversation was not created");
    const newChannel = client.channel("messaging", channelID);
    await newChannel.watch();
    await newChannel.sendMessage({ text });
    setActiveChannel?.(newChannel);
    setChannel(newChannel);
    setComposing(false);
  };

  // "Schedule message" from the "New message" pane: create (or reuse) the
  // conversation now, schedule the text into it, and open it so the banner
  // shows what's waiting. Errors propagate to the composer.
  const handleScheduleConversation = async (recipients, text, sendAt, clientMessageId) => {
    if (!isClientConnected(client)) {
      throw new Error("Chat is not connected");
    }
    const response = await postRightworkAPI(API_create_chat_channel, {
      channel_type: "messaging",
      member_ids: recipients.map((recipient) => recipient.id.toString()),
      store_id: props.homeStore,
    });
    const channelID = response?.data?.redux?.payload?.channel_id;
    if (!channelID) throw new Error("Conversation was not created");
    const row = await scheduledMessagesState.schedule({
      channelType: "messaging",
      channelId: channelID,
      sendAt,
      message: { text },
      clientMessageId,
    });
    // A retry of a request that already went through returns the original
    // row, so confirm the time that was actually saved.
    toast.success(`Scheduled for ${formatScheduledTime(row?.send_at || sendAt)}`, {
      autoClose: 3000,
    });
    // The message is scheduled from here on: opening the conversation is a
    // nicety, and a failure must not read as a failed schedule (a retry
    // would schedule it twice).
    setComposing(false);
    const token = ++navigationToken.current;
    try {
      const newChannel = client.channel("messaging", channelID);
      await newChannel.watch();
      if (token !== navigationToken.current) return;
      setActiveChannel?.(newChannel);
      setChannel(newChannel);
    } catch (error) {
      console.error("Error opening scheduled conversation:", error);
    }
  };

  // Scheduled list: open the conversation a scheduled message goes to.
  const openScheduledChannel = async (row) => {
    if (!isClientConnected(client)) return;
    const token = ++navigationToken.current;
    try {
      const target = client.channel(row.channel_type, row.channel_id);
      await target.watch();
      if (token !== navigationToken.current) return;
      setActiveChannel?.(target);
      setChannel(target);
    } catch (error) {
      if (token !== navigationToken.current) return;
      toast.error(
        scheduledMessageErrorText(error, "We couldn't open this conversation."),
        { autoClose: 3000 }
      );
    }
  };

  // Search results: channels open directly; people go through the
  // server-authorized DM path (which also reuses legacy conversations)
  // instead of Stream's default client-side distinct channel creation.
  const handleSelectSearchResult = async (params, result) => {
    params?.setQuery?.("");
    params?.setResults?.([]);
    setComposing(false);
    if (result?.cid) {
      if (!isClientConnected(client)) return;
      try {
        await result.watch?.();
      } catch (error) {
        console.error("Error opening search result:", error);
      }
      setActiveChannel?.(result);
      setChannel(result);
      return;
    }
    await handleCreateDM(result);
  };

  const openDetails = (targetChannel) => {
    if (!targetChannel) return;
    setShowTooltip(false);
    setDetailsChannel(targetChannel);
  };
  const closeDetails = () => setDetailsChannel(null);

  const closeConversation = () => {
    closeDetails();
    setChannel(null);
    setActiveChannel?.(null);
  };

  const promoteChannelModerator = (targetChannel, userId) =>
    postRightworkAPI(API_promote_channel_moderator, {
      channel_id: targetChannel.id,
      channel_type: targetChannel.type,
      user_id: userId.toString(),
      store_id: props.homeStore,
    });

  const demoteChannelModerator = (targetChannel, userId) =>
    postRightworkAPI(API_demote_channel_moderator, {
      channel_id: targetChannel.id,
      channel_type: targetChannel.type,
      user_id: userId.toString(),
      store_id: props.homeStore,
    });

  // Details-sheet actions. They throw on failure so the sheet can show the
  // error inline; the sheet re-renders from live channel events.
  const handleEditChannel = async (targetChannel, updatedName, updatedDescription) => {
    await targetChannel.updatePartial({
      set: { name: updatedName, description: updatedDescription || "" },
    });
  };

  const MODERATOR_ROLES = ["company_manager", "company_admin", "rightwork_internal"];

  const handleAddMembersToChannel = async (targetChannel, members) => {
    const memberIds = members.map((member) => member.id.toString());
    await targetChannel.addMembers(memberIds);
    // Managers and above become moderators automatically
    const membersToPromote = members.filter((member) =>
      MODERATOR_ROLES.includes(member?.permission_type)
    );
    for (const member of membersToPromote) {
      try {
        await promoteChannelModerator(targetChannel, member.id);
      } catch (err) {
        console.error("Error auto-promoting member:", err);
      }
    }
    await targetChannel.query();
  };

  const handleAddPeopleToDirectMessage = async (targetChannel, members) => {
    const response = await postRightworkAPI(API_add_direct_message_members, {
      channel_id: targetChannel.id,
      member_ids: members.map((member) => member.id.toString()),
      store_id: props.homeStore,
    });
    const payload = response?.data?.redux?.payload || {};
    if (payload.created_new && payload.channel_id) {
      // Adding to a 1:1 starts a new group; move over to it
      const newChannel = client.channel("messaging", payload.channel_id);
      await newChannel.watch();
      closeDetails();
      setActiveChannel?.(newChannel);
      setChannel(newChannel);
      return;
    }
    await targetChannel.query();
  };

  const handleRemoveMemberFromChannel = async (targetChannel, userId) => {
    // Server-side so the last-manager guard applies
    await postRightworkAPI(API_remove_chat_channel_member, {
      channel_id: targetChannel.id,
      channel_type: targetChannel.type,
      user_id: userId.toString(),
      store_id: props.homeStore,
    });
    await targetChannel.query();
  };

  const handleDeleteChannel = async (targetChannel) => {
    // Server-side so a moderator can delete a channel whose creator has left
    await postRightworkAPI(API_delete_chat_channel, {
      channel_id: targetChannel.id,
      channel_type: targetChannel.type,
      store_id: props.homeStore,
    });
    closeConversation();
  };

  const handleLeaveConversation = async (targetChannel) => {
    await postRightworkAPI(API_leave_chat_channel, {
      channel_id: targetChannel.id,
      channel_type: targetChannel.type,
      store_id: props.homeStore,
    });
    closeConversation();
  };

  const handlePromoteToModerator = async (targetChannel, userId) => {
    await promoteChannelModerator(targetChannel, userId);
    await targetChannel.query();
  };

  const handleDemoteFromModerator = async (targetChannel, userId) => {
    await demoteChannelModerator(targetChannel, userId);
    await targetChannel.query();
  };

  const handleMuteChannel = async (targetChannel) => {
    await targetChannel.mute();
  };

  const handleUnmuteChannel = async (targetChannel) => {
    await targetChannel.unmute();
  };

  const handleTogglePin = async (targetChannel) => {
    if (targetChannel.state?.membership?.pinned_at) {
      await targetChannel.unpin();
    } else {
      await targetChannel.pin();
    }
  };

  const userHasChannelModeratorPrivileges = React.useCallback((activeChannel) => {
    if (!activeChannel || !client?.userID) return false;

    const currentUserMember = Object.values(activeChannel?.state?.members || {})
      .find((member) => member.user?.id === client.userID);
    const isOwner = activeChannel?.data?.created_by?.id === client.userID;
    const isModerator = currentUserMember?.channel_role === "channel_moderator";

    return isOwner || isModerator;
  }, [client?.userID]);

  const userCanModerateMessages = React.useCallback((activeChannel) => {
    const isAdmin = ADMIN_PERMISSION_TYPES.includes(props.permissionRole);

    return userHasChannelModeratorPrivileges(activeChannel) || isAdmin;
  }, [props.permissionRole, userHasChannelModeratorPrivileges]);

  const canModerateMessage = React.useCallback((message) => (
    Boolean(message?.id) &&
    message?.type !== "deleted" &&
    userCanModerateMessages(channel)
  ), [channel, userCanModerateMessages]);

  const canShowCustomDeleteMessage = React.useCallback((message) => (
    canModerateMessage(message)
  ), [canModerateMessage]);

  const dispatchDeletedMessageEvent = React.useCallback((activeChannel, deletedMessage) => {
    if (!activeChannel || !deletedMessage || typeof client?.dispatchEvent !== "function") {
      return;
    }

    client.dispatchEvent({
      cid: activeChannel.cid,
      channel_id: activeChannel.id,
      channel_type: activeChannel.type,
      created_at: new Date().toISOString(),
      message: deletedMessage,
      type: "message.deleted",
    });
  }, [client]);

  const deleteChannelMessage = React.useCallback(async (message) => {
    if (!channel || !message?.id) {
      throw new Error("Cannot delete message without an active channel.");
    }

    const res = await postRightworkAPI(API_delete_channel_message, {
      channel_id: channel.id,
      channel_type: channel.type,
      message_id: message.id,
      store_id: props.homeStore,
    });
    const responseMessage = res?.data?.redux?.payload?.message;
    return buildDeletedMessage(message, responseMessage);
  }, [channel, props.homeStore]);

  const handleDeleteMessageRequest = React.useCallback(async (message) => {
    return deleteChannelMessage(message);
  }, [deleteChannelMessage]);

  const handleModeratorDeleteMessage = React.useCallback(async (message, event) => {
    event?.preventDefault?.();

    if (!canModerateMessage(message)) {
      return;
    }

    const confirmed = window.confirm("Delete this message for everyone in the channel?");
    if (!confirmed) {
      return;
    }

    try {
      const deletedMessage = await deleteChannelMessage(message);
      dispatchDeletedMessageEvent(channel, deletedMessage);
    } catch (error) {
      console.error("Error deleting channel message:", error);
      window.alert("Unable to delete message. Please try again.");
    }
  }, [canModerateMessage, channel, deleteChannelMessage, dispatchDeletedMessageEvent]);

  const customMessageActions = React.useMemo(() => (
    userCanModerateMessages(channel)
      ? { "Delete message": handleModeratorDeleteMessage }
      : undefined
  ), [channel, handleModeratorDeleteMessage, userCanModerateMessages]);

  const ModeratorCustomMessageActionsList = React.useCallback(({ customMessageActions, message }) => {
    if (!customMessageActions || !canShowCustomDeleteMessage(message)) {
      return null;
    }

    return (
      <>
        {Object.keys(customMessageActions).map((customAction) => (
          <button
            aria-selected="false"
            className="str-chat__message-actions-list-item str-chat__message-actions-list-item-button !text-rose-700"
            key={customAction}
            onClick={(event) => customMessageActions[customAction](message, event)}
            role="option"
          >
            {customAction}
          </button>
        ))}
      </>
    );
  }, [canShowCustomDeleteMessage]);

  // Right side of the header: pinned messages, mute, details
  const HeaderActions = ({ channel: headerChannel }) => {
    const { client: headerClient } = useChatContext();
    const { dispatch, jumpToMessage, openThread } = useChannelActionContext();
    const [pinAnchor, setPinAnchor] = useState(null);
    const pinned = headerChannel?.state?.pinnedMessages || [];
    // Thread replies live in channel.state.threads[parent_id], never in the
    // root message list, so jumpToMessage (which only searches root messages)
    // can't reach a pinned reply. Load it into its thread and open that.
    const jumpToPinnedMessage = async (pinnedMessage) => {
      if (!pinnedMessage.parent_id) {
        await jumpToMessage(pinnedMessage.id);
        return;
      }
      // Jump the root list to the parent first: that keeps Stream's React
      // state in sync with the message set it switches to, and leaves the
      // parent highlighted behind the thread pane
      await jumpToMessage(pinnedMessage.parent_id);
      const parent =
        headerChannel.state.messages.find(
          (message) => message.id === pinnedMessage.parent_id
        ) || headerChannel.state.findMessage(pinnedMessage.parent_id);
      if (!parent) return;
      // Pull the pinned reply into threads[parent_id] so the pane opens with
      // it already loaded, however old it is
      await headerChannel.state.loadMessageIntoState(
        pinnedMessage.id,
        pinnedMessage.parent_id
      );
      openThread(parent);
      // Opening the thread alone lands on its latest replies, so target the
      // pinned reply too. The thread list scrolls to whatever
      // highlightedMessageId names, but its effect reads a container element
      // held in state, which is still null on the mount pass and doesn't
      // re-run when it lands: set the target only once the pane has rendered.
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      dispatch({
        channel: headerChannel,
        highlightedMessageId: pinnedMessage.id,
        type: "jumpToMessageFinished",
      });
    };
    // muteStatus() reads client state that doesn't trigger a re-render on
    // its own, so mirror it locally and refresh on Stream's mute events
    const [isMuted, setIsMuted] = useState(
      () => Boolean(headerChannel?.muteStatus?.()?.muted)
    );
    useEffect(() => {
      setIsMuted(Boolean(headerChannel?.muteStatus?.()?.muted));
      if (!headerClient) return undefined;
      const sync = () =>
        setIsMuted(Boolean(headerChannel?.muteStatus?.()?.muted));
      const subscription = headerClient.on("notification.channel_mutes_updated", sync);
      return () => subscription?.unsubscribe?.();
    }, [headerChannel, headerClient]);
    // Same treatment for the conversation pin (membership.pinned_at)
    const [isPinned, setIsPinned] = useState(
      () => Boolean(headerChannel?.state?.membership?.pinned_at)
    );
    useEffect(() => {
      setIsPinned(Boolean(headerChannel?.state?.membership?.pinned_at));
      if (!headerChannel) return undefined;
      const sync = () =>
        setIsPinned(Boolean(headerChannel?.state?.membership?.pinned_at));
      const subscription = headerChannel.on("member.updated", sync);
      return () => subscription?.unsubscribe?.();
    }, [headerChannel]);
    const iconButtonBase =
      "flex h-8 w-8 items-center justify-center rounded-lg border";
    const iconButton = `${iconButtonBase} border-slate-200 text-slate-600 hover:bg-slate-50`;
    const iconButtonActive = `${iconButtonBase} border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100`;

    return (
      <div className="flex shrink-0 items-center gap-x-1.5">
        {pinned.length > 0 && (
          <Tooltip title="Pinned messages">
            <button
              type="button"
              className={`${iconButton} relative`}
              onClick={(event) => setPinAnchor(event.currentTarget)}
              aria-label="Pinned messages"
            >
              <BookmarkBorderIcon style={{ fontSize: "18px" }} />
              <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-violet-700 px-1 text-xxs text-white">
                {pinned.length}
              </span>
            </button>
          </Tooltip>
        )}
        <Tooltip title={isPinned ? "Unpin from top" : "Pin to top"}>
          <button
            type="button"
            className={isPinned ? iconButtonActive : iconButton}
            onClick={() => {
              const next = !isPinned;
              setIsPinned(next);
              handleTogglePin(headerChannel).catch((error) => {
                console.error("Error toggling pin:", error);
                setIsPinned(!next);
              });
            }}
            aria-label={isPinned ? "Unpin conversation" : "Pin conversation"}
          >
            {isPinned ? (
              <PushPinIcon style={{ fontSize: "18px" }} />
            ) : (
              <PushPinOutlinedIcon style={{ fontSize: "18px" }} />
            )}
          </button>
        </Tooltip>
        <Tooltip title={isMuted ? "Unmute" : "Mute"}>
          <button
            type="button"
            className={isMuted ? iconButtonActive : iconButton}
            onClick={() => {
              const next = !isMuted;
              setIsMuted(next);
              (next ? handleMuteChannel(headerChannel) : handleUnmuteChannel(headerChannel))
                .catch((error) => {
                  console.error("Error toggling mute:", error);
                  setIsMuted(!next);
                });
            }}
            aria-label={isMuted ? "Unmute conversation" : "Mute conversation"}
          >
            {isMuted ? (
              <VolumeOffIcon style={{ fontSize: "18px" }} />
            ) : (
              <VolumeUpIcon style={{ fontSize: "18px" }} />
            )}
          </button>
        </Tooltip>
        <Tooltip title="Details">
          <button
            type="button"
            className={iconButton}
            onClick={() => openDetails(headerChannel)}
            aria-label="Conversation details"
          >
            <InfoOutlinedIcon style={{ fontSize: "18px" }} />
          </button>
        </Tooltip>
        <Popover
          open={Boolean(pinAnchor)}
          anchorEl={pinAnchor}
          onClose={() => setPinAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <div className="w-80">
            <div className="border-b border-slate-100 px-4 py-3">
              <p className="text-sm font-semibold text-slate-900">Pinned messages</p>
            </div>
            <div className="max-h-80 overflow-y-auto py-1">
              {pinned.map((pinnedMessage) => (
                <button
                  key={pinnedMessage.id}
                  type="button"
                  className="block w-full px-4 py-2 text-left hover:bg-slate-50"
                  onClick={() => {
                    setPinAnchor(null);
                    jumpToPinnedMessage(pinnedMessage).catch((error) =>
                      console.error("Error jumping to pinned message:", error)
                    );
                  }}
                >
                  <div className="flex items-center justify-between gap-x-2">
                    <span className="truncate text-xs font-semibold text-slate-700">
                      {formatName(pinnedMessage.user?.name)}
                    </span>
                    <span className="shrink-0 text-xxs text-slate-400">
                      {formatMessageTimestamp(new Date(pinnedMessage.created_at))}
                    </span>
                  </div>
                  <p className="mt-0.5 truncate text-sm text-slate-600">
                    {pinnedMessage.text ||
                      (pinnedMessage.attachments?.length ? "Attachment" : "")}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </Popover>
      </div>
    );
  };

  const CustomChannelHeader = (props) => {
    const { client } = useChatContext();
    const { channel, watcher_count } = useChannelStateContext();
    const member = Object.values(channel.state.members).filter(
      ({ user }) => user.id !== client.userID
    );

    const channelIsAutomated = isAutomatedChannel(channel);
    const headerSpecial = getSpecialTeamChannelStyle(channel);

    const getWatcherText = (watchers) => {
      if (!watchers) return "0 online";
      if (watchers === 1) return "1 online";
      return `${watchers} online`;
    };

    return (
      <div className="py-4 px-6 border-b border-slate-100 flex items-center gap-x-3">
        <div className="min-w-0 flex-1">
        {headerSpecial ? (
          <div className="flex items-center justify-between">
            <div
              className="flex gap-x-3 relative cursor-pointer"
              onClick={() => openDetails(channel)}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <div className={`w-10 h-10 ${headerSpecial.bg} text-white text-xl rounded-full flex items-center justify-center`}>
                <headerSpecial.Icon style={{ fontSize: "22px" }}/>
              </div>
              <div className="min-w-0">
                <ChannelNameWithBadge
                  title={getChannelDisplayName(channel, client.userID, todayDateStr, dailyStoreNames)}
                  automated={channelIsAutomated}
                  titleClassName="text-[18px] leading-[18px]"
                />
                <div className="flex items-center gap-x-2 mt-0.5">
                  <p className="text-sm text-slate-500">
                    {channel?.data?.member_count} members,
                  </p>
                  <p className="text-sm text-slate-500">
                    {getWatcherText(watcher_count)}
                  </p>
                </div>
              </div>
              {showTooltip && (
                <div className="absolute top-10 left-10 z-50">
                  <CustomChannelMembersTooltip channel={channel} />
                </div>
              )}
            </div>
          </div>
        ) : channel?.type === "team" ? (
          <div className="flex items-center justify-between">
            <div
              className="flex gap-x-3 relative cursor-pointer"
              onClick={() => openDetails(channel)}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <div
                className={`w-10 h-10 text-white text-xl rounded-full flex items-center justify-center ${
                  channelIsAutomated ? "bg-teal-600" : "bg-orange-600"
                }`}
              >
                {channelIsAutomated ? (
                  <AutomatedChannelIcon />
                ) : (
                  "#"
                )}
              </div>
              <div className="min-w-0">
                <ChannelNameWithBadge
                  title={getChannelDisplayName(channel, client.userID, todayDateStr, dailyStoreNames)}
                  automated={channelIsAutomated}
                  titleClassName="text-[18px] leading-[18px]"
                />
                <div className="flex items-center gap-x-2 mt-0.5">
                  <p className="text-sm text-slate-500">
                    {channel?.data?.member_count} members,
                  </p>
                  <p className="text-sm text-slate-500">
                    {getWatcherText(watcher_count)}
                  </p>
                </div>
              </div>
              {showTooltip && (
                <div className="absolute top-10 left-10 z-50">
                  <CustomChannelMembersTooltip channel={channel} />
                </div>
              )}
            </div>
          </div>
        ) : channel?.type === "announcement" ? (
          <div className="flex items-center justify-between">
            <div
              className="flex gap-x-3 relative cursor-pointer"
              onClick={() => openDetails(channel)}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <div className="w-10 h-10 bg-gray-700 text-white text-xl rounded-full flex items-center justify-center">
                <CampaignIcon style={{ fontSize: "22px" }}/>
              </div>
              <div className="min-w-0">
                <ChannelNameWithBadge
                  title={getChannelDisplayName(channel, client.userID, todayDateStr, dailyStoreNames)}
                  automated={channelIsAutomated}
                  titleClassName="text-[18px] leading-[18px]"
                />
                <div className="flex items-center gap-x-2 mt-0.5">
                  <p className="text-sm text-slate-500">
                    {channel?.data?.member_count} members,
                  </p>
                  <p className="text-sm text-slate-500">
                    {getWatcherText(watcher_count)}
                  </p>
                </div>
              </div>
              {showTooltip && (
                <div className="absolute top-10 left-10 z-50">
                  <CustomChannelMembersTooltip channel={channel} />
                </div>
              )}
            </div>
          </div>
        ) : channel?.type === "daily" ? (
          <div className="flex items-center justify-between">
            <div
              className="flex min-w-0 max-w-full gap-x-3 relative cursor-pointer"
              onClick={() => openDetails(channel)}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <div className="shrink-0 w-10 h-10 bg-blue-600 text-white text-xl rounded-full flex items-center justify-center">
                <CalendarTodayIcon style={{ fontSize: "22px" }}/>
              </div>
              <div className="min-w-0">
                <ChannelNameWithBadge
                  title={getChannelDisplayName(channel, client.userID, todayDateStr, dailyStoreNames)}
                  automated={channelIsAutomated}
                  titleClassName="text-[18px] leading-[18px]"
                />
                <div className="flex items-center gap-x-2 mt-0.5">
                  <p className="text-sm text-slate-500">
                    {channel?.data?.member_count} members,
                  </p>
                  <p className="text-sm text-slate-500">
                    {getWatcherText(watcher_count)}
                  </p>
                </div>
              </div>
              {showTooltip && (
                <div className="absolute top-10 left-10 z-50">
                  <CustomChannelMembersTooltip channel={channel} />
                </div>
              )}
            </div>
          </div>
        ) : isGroupDirectMessage(channel) ? (
          <div className="flex items-center justify-between">
            <div
              className="flex gap-x-3 relative cursor-pointer"
              onClick={() => openDetails(channel)}
            >
              <GroupDirectMessageAvatar
                members={getDirectMessageOtherMembers(channel, client.userID)}
              />
              <div className="min-w-0">
                <p className="text-[18px] leading-[18px] truncate">
                  {getChannelDisplayName(channel, client.userID, todayDateStr, dailyStoreNames)}
                </p>
                <div className="flex items-center gap-x-2 mt-0.5">
                  <p className="text-sm text-slate-500">
                    {channel?.data?.member_count} members,
                  </p>
                  <p className="text-sm text-slate-500">
                    {getWatcherText(watcher_count)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="flex gap-x-3 cursor-pointer"
            onClick={() => openDetails(channel)}
          >
            <div className="w-10 h-10 bg-violet-700 text-white text-xl rounded-full flex items-center justify-center">
              {member?.[0]?.user?.name?.[0]}
            </div>
            <div>
              <p className="text-[18px] leading-[18px]">
                {member?.[0]?.user?.name}
              </p>
              {member?.[0]?.user?.online ? (
                <div className="flex items-center gap-x-1 mt-0.5">
                  <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full"></div>
                  <p className="text-sm text-slate-500">Online</p>
                </div>
              ) : (
                <div className="flex items-center gap-x-1 mt-0.5">
                  <div className="w-2.5 h-2.5 bg-slate-300 rounded-full"></div>
                  <p className="text-sm text-slate-500">Offline</p>
                </div>
              )}
            </div>
          </div>
        )}
        </div>
        <HeaderActions channel={channel} />
      </div>
    );
  };

  const CustomChannelPreview = (props) => {
    const {
      channel,
      activeChannel,
      displayImage,
      displayTitle: streamDisplayTitle,
      lastMessage,
      latestMessage,
      setActiveChannel,
      type,
    } = props;
    const displayTitle = getChannelDisplayName(channel, userID, todayDateStr, dailyStoreNames) || streamDisplayTitle;
    const senderPrefix = getPreviewSenderPrefix(channel, lastMessage, userID);
    const latestMessageAt = channel.state.last_message_at;
    const isSelected = channel.id === activeChannel?.id;
    const { userLanguage } = useTranslationContext();
    const isChannel = channel.type === "team";
    const special = getSpecialTeamChannelStyle(channel);
    const isAnnouncement = channel.type === "announcement";
    const isDaily = channel.type === "daily";
    const channelIsAutomated = isAutomatedChannel(channel);
    const unreadMessageCount = channel.state.unreadCount;
    // Presence comes from Stream's user.online (kept live because the list
    // is queried with presence: true), not from who has the channel open
    const dmReceiverOnline = Boolean(
      getDirectMessageOtherMembers(channel, userID)[0]?.user?.online
    );

    const timestamp = React.useMemo(() => {
      if (!latestMessageAt) {
        return "";
      }

      const now = new Date();
      const messageDate = new Date(latestMessageAt);

      const isToday = now.toDateString() === messageDate.toDateString();
      const isYesterday =
        new Date(now.setDate(now.getDate() - 1)).toDateString() ===
        messageDate.toDateString();

      if (isToday) {
        const formatter = new Intl.DateTimeFormat(userLanguage, {
          timeStyle: "short",
        });
        return formatter.format(messageDate);
      } else if (isYesterday) {
        return "Yesterday";
      } else {
        const formatter = new Intl.DateTimeFormat(userLanguage, {
          month: "numeric",
          day: "numeric",
          year: "2-digit",
        });
        return formatter.format(messageDate);
      }
    }, [latestMessageAt, userLanguage]);

    const handleClick = () => {
      if (!isClientConnected(client)) return;
      setComposing(false);
      setActiveChannel?.(channel);
      setChannel(channel);
      // Explicitly mark the channel as read so unread counts
      // update immediately in the sidebar
      channel.markRead?.().catch((err) => {
        console.error("Error marking channel read", err);
      });
    };

    return (
      <div
        className={`group relative py-2.5 px-4 hover:bg-slate-100 cursor-pointer ${
          isSelected ? "bg-slate-200" : "bg-white"
        }`}
        disabled={isSelected}
        onClick={handleClick}
      >
        {special ? (
          <div className="flex gap-x-3">
            <div className={`shrink-0 w-7 h-7 ${special.bg} text-white rounded-full flex items-center justify-center mt-1`}>
              <special.Icon style={{ fontSize: "16px" }} />
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <ChannelPreviewText
                title={channel?.data?.name}
                latestMessage={latestMessage}
                latestMessageAt={latestMessageAt}
                timestamp={timestamp}
                unreadMessageCount={unreadMessageCount}
                senderPrefix={senderPrefix}
              />
            </div>
          </div>
        ) : isChannel ? (
          <div className="flex gap-x-3">
            <div
              className={`shrink-0 w-7 h-7 text-white rounded-full flex items-center justify-center mt-1 ${
                channelIsAutomated ? "bg-teal-600" : "bg-orange-600"
              }`}
            >
              {channelIsAutomated ? (
                <AutomatedChannelIcon size={15} />
              ) : (
                "#"
              )}
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <ChannelPreviewText
                title={channel?.data?.name}
                latestMessage={latestMessage}
                latestMessageAt={latestMessageAt}
                timestamp={timestamp}
                unreadMessageCount={unreadMessageCount}
                senderPrefix={senderPrefix}
              />
            </div>
          </div>
        ) : isAnnouncement ? (
          <div className="flex gap-x-3">
            <div className="shrink-0 w-7 h-7 bg-gray-700 text-white rounded-full flex items-center justify-center mt-1 relative">
              <CampaignIcon style={{ fontSize: "18px" }} />
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <ChannelPreviewText
                title={displayTitle}
                latestMessage={latestMessage}
                latestMessageAt={latestMessageAt}
                timestamp={timestamp}
                unreadMessageCount={unreadMessageCount}
                senderPrefix={senderPrefix}
              />
            </div>
          </div>
        ) : isDaily ? (
          <div className="flex gap-x-3">
            <div className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center mt-1 relative">
              <CalendarTodayIcon style={{ fontSize: "18px" }} />
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <ChannelPreviewText
                title={displayTitle}
                latestMessage={latestMessage}
                latestMessageAt={latestMessageAt}
                timestamp={timestamp}
                unreadMessageCount={unreadMessageCount}
                senderPrefix={senderPrefix}
              />
            </div>
          </div>
        ) : (
          <div className="flex gap-x-3">
            {isGroupDirectMessage(channel) ? (
              <div className="mt-1">
                <GroupDirectMessageAvatar
                  members={getDirectMessageOtherMembers(channel, userID)}
                  size={28}
                />
              </div>
            ) : (
            <div className="shrink-0 w-7 h-7 bg-violet-700 text-white rounded-full flex items-center justify-center mt-1 relative">
              {displayTitle ? displayTitle[0] : ""}
              <div
                className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ${
                  dmReceiverOnline ? "bg-emerald-500" : "bg-slate-300"
                } border border-white`}
              ></div>
            </div>
            )}
            <div className="min-w-0 flex-1 overflow-hidden">
              <ChannelPreviewText
                title={displayTitle}
                latestMessage={latestMessage}
                latestMessageAt={latestMessageAt}
                timestamp={timestamp}
                unreadMessageCount={unreadMessageCount}
                senderPrefix={senderPrefix}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const CustomSearchResultItem = (props) => {
    const { result, selectResult } = props;
    const isStreamChannel = !!result.cid;
    const isChannel = isStreamChannel && result.type === "team";
    const special = getSpecialTeamChannelStyle(result);
    const isAnnouncement = isStreamChannel && result.type === "announcement";
    const isDaily = isStreamChannel && result.type === "daily";
    const resultTitle = isStreamChannel
      ? getChannelSearchTitle(result)
      : result.name ?? result.id;
    const directMessageUserId = getDirectMessageUserId(result, userID);
    const resultEmail = isStreamChannel
      ? chatUsers.find(
          (chatUser) => chatUser.id?.toString() === directMessageUserId
        )?.email
      : result.email;
    const channelIsAutomated = isAutomatedChannel(result);

    return (
      <div
        className={`py-4 px-6 hover:bg-slate-100 cursor-pointer`}
        onClick={() => selectResult(result)}
      >
        {special ? (
          <div className="flex gap-x-3">
            <div className={`w-7 h-7 ${special.bg} text-white rounded-full flex items-center justify-center`}>
              <special.Icon style={{ fontSize: "16px" }} />
            </div>
            <p>{resultTitle}</p>
          </div>
        ) : isChannel ? (
          <div className="flex gap-x-3">
            <div
              className={`w-7 h-7 text-white rounded-full flex items-center justify-center ${
                channelIsAutomated ? "bg-teal-600" : "bg-orange-600"
              }`}
            >
              {channelIsAutomated ? (
                <AutomatedChannelIcon size={15} />
              ) : (
                "#️"
              )}
            </div>
            <ChannelNameWithBadge
              title={resultTitle}
              automated={channelIsAutomated}
            />
          </div>
        ) : isAnnouncement ? (
          <div className="flex gap-x-3">
            <div className="w-7 h-7 bg-gray-700 text-white rounded-full flex items-center justify-center">
              <CampaignIcon style={{ fontSize: "18px" }} />
            </div>
            <ChannelNameWithBadge
              title={resultTitle}
              automated={channelIsAutomated}
            />
          </div>
        ) : isDaily ? (
          <div className="flex gap-x-3">
            <div className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center">
              <CalendarTodayIcon style={{ fontSize: "18px" }} />
            </div>
            <ChannelNameWithBadge
              title={resultTitle}
              automated={channelIsAutomated}
            />
          </div>
        ) : (
          <div className="flex gap-x-3">
            <div className="w-7 h-7 bg-violet-700 text-white rounded-full flex items-center justify-center">
              {resultTitle ? resultTitle[0] : ""}
            </div>
            <div className="min-w-0">
              <p>{resultTitle}</p>
              {resultEmail && (
                <p className="truncate text-xs text-slate-500">
                  {resultEmail}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Hover card for channel headers, styled like the details sheet
  const CustomChannelMembersTooltip = (props) => {
    const hoverChannel = props.channel;
    const members = Object.values(hoverChannel?.state?.members || {});
    const channelCreatedById = hoverChannel?.data?.created_by?.id;
    const totalMembers = hoverChannel?.data?.member_count ?? members.length;
    const onlineCount = members.filter((member) => member.user?.online).length;
    const isDm = hoverChannel?.type === "messaging";
    const MAX_ROWS = 12;

    // Owner, moderators, then online, then name
    const rolePriority = (member) => {
      if (!isDm && member.user?.id === channelCreatedById) return 0;
      if (!isDm && member.channel_role === "channel_moderator") return 1;
      return 2;
    };
    const sorted = [...members].sort(
      (a, b) =>
        rolePriority(a) - rolePriority(b) ||
        Number(Boolean(b.user?.online)) - Number(Boolean(a.user?.online)) ||
        (a.user?.name || "").localeCompare(b.user?.name || "")
    );
    const shown = sorted.slice(0, MAX_ROWS);
    const hiddenCount = Math.max(0, totalMembers - shown.length);

    return (
      <div className="w-80 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-xl">
        <div className="px-4 pb-3 pt-4">
          <p className="truncate text-base font-semibold text-slate-900">
            {getChannelDisplayName(hoverChannel, client.userID, todayDateStr, dailyStoreNames)}
          </p>
          <p className="mt-0.5 text-sm text-slate-500">
            {totalMembers} {totalMembers === 1 ? "member" : "members"} · {onlineCount} online
          </p>
          {hoverChannel?.data?.description && (
            <p
              className="mt-2 overflow-hidden text-sm text-slate-700"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
              }}
            >
              {hoverChannel.data.description}
            </p>
          )}
        </div>
        <div className="max-h-[360px] overflow-y-auto border-t border-slate-100 px-4 py-2">
          {shown.map((member) => {
            const user = member.user || {};
            const isOwner = !isDm && user.id === channelCreatedById;
            const isModerator = !isDm && member.channel_role === "channel_moderator";
            return (
              <div key={user.id} className="flex items-center gap-x-3 py-1.5">
                <div className="relative">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-violet-700 text-xs text-white">
                    {user.name?.trim()?.[0]?.toUpperCase() || "?"}
                  </div>
                  <span
                    className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white ${
                      user.online ? "bg-emerald-500" : "bg-slate-300"
                    }`}
                  />
                </div>
                <span className="min-w-0 flex-1 truncate text-sm text-slate-900">
                  {formatName(user.name)}
                  {user.id === client.userID && (
                    <span className="text-slate-400"> (you)</span>
                  )}
                </span>
                {isOwner && (
                  <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-xxs text-slate-600">
                    Owner
                  </span>
                )}
                {isModerator && (
                  <span className="shrink-0 rounded bg-violet-100 px-1.5 py-0.5 text-xxs text-violet-700">
                    Moderator
                  </span>
                )}
              </div>
            );
          })}
          {hiddenCount > 0 && (
            <p className="py-1.5 text-xs text-slate-500">
              +{hiddenCount} more
            </p>
          )}
        </div>
        <div className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-xs text-slate-500">
          Click for details
        </div>
      </div>
    );
  };

  if (!client || !userID) {
    return (
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <LoadSpinner />
      </div>
    );
  }

  // const canShowChannelSearch =
  //   props.permissionRole !== "company_employee" &&
  //   props.permissionRole !== "company_manager";

  const canShowChannelSearch = true


  return (
    <ScheduledMessagesProvider value={scheduledMessagesState}>
    <RevealedTimestampsContext.Provider value={revealedTimestampsContextValue}>
    <ReadReceiptsPolicyContext.Provider value={readReceiptsPolicy}>
    <ActiveChannelReadReceiptsProvider channel={channel}>
      <div className="w-full h-screen flex">
        <div className="sidebar">
          <ChannelList
            // While a deep link is opening a DM, don't let the list activate
            // its first channel in the meantime.
            setActiveChannelOnMount={!props.pendingChatTarget}
            showChannelSearch={canShowChannelSearch}
            additionalChannelSearchProps={{
              placeholder: "Search people and channels",
              searchForChannels: true,
              SearchResultItem: CustomSearchResultItem,
              searchFunction: searchChannelsAndUsers,
              onSelectResult: handleSelectSearchResult,
            }}
            Preview={CustomChannelPreview}
            Paginator={AutoLoadPaginator}
            renderChannels={(loadedChannels, renderChannel) => (
              <ChatSidebarSections
                channels={loadedChannels}
                renderChannel={renderChannel}
              />
            )}
            sendChannelsToList
            sort={sort}
            filters={filters}
            options={options}
          />
          {(props.permissionRole !== "company_employee" ||
            (props.permissionRole === "company_employee" &&
              props.employeeCreateChatEnabled)) &&
            (props.permissionRole !== "company_manager" ||
              (props.permissionRole === "company_manager" &&
                props.managerCreateChatEnabled)) && (
              <div className="w-full flex items-center justify-between mb-2 px-4 border-r border-slate-100">
                <div
                  className="bg-violet-50 hover:bg-violet-100 text-violet-700 w-full py-2.5 rounded-lg flex items-center justify-center gap-x-2 cursor-pointer"
                  onClick={() => {
                    setChannel(null);
                    setActiveChannel?.(null);
                    setShowScheduled(false);
                    setComposing(true);
                  }}
                >
                  <div>
                    <AddIcon />
                  </div>
                  <p>New message</p>
                </div>
              </div>
            )}
        </div>
        <div className="chat-window">
          {showScheduled ? (
            <ScheduledMessagesPanel onOpenChannel={openScheduledChannel} />
          ) : composing ? (
            <NewMessageComposer
              key={props.homeStore}
              chatUsers={chatUsers}
              onSend={handleStartConversation}
              onSchedule={handleScheduleConversation}
              canCreateChannel={props.permissionRole !== "company_employee"}
              onCreateChannel={(recipients = []) => {
                setModalEdit(false);
                setModalType("Channel");
                setChannelName("");
                setChannelDescription("");
                // Carry the people already picked over to the channel form
                setSelectedMembers(
                  recipients.filter(
                    (user) => user.can_join_store_channel !== false
                  )
                );
                setShowCreateChannel(true);
              }}
            />
          ) : (
          <>
          {/* Force Channel subtree to remount when switching channels so any open thread state is cleared */}
          <Channel
            key={channel?.cid ?? "no-channel"}
            channel={channel}
            reactionOptions={reactionOptions}
            ReactionSelector={FullReactionSelector}
            EmojiPicker={EmojiPicker}
            CustomMessageActionsList={ModeratorCustomMessageActionsList}
            doDeleteMessageRequest={handleDeleteMessageRequest}
            MessageStatus={MessageStatusWithViewers}
            MessageTimestamp={MessageTimestampWithViewers}
            MessageRepliesCountButton={ThreadSummaryButton}
            Message={MessageSimpleWithRevealableTimestamps}
            SendButton={ScheduleSendButton}
          >
            <ReactionOptionsHistorySync onSync={refreshReactionOptions} />
            <WithComponents overrides={messageListComponents}>
            <Window>
              <CustomChannelHeader />
              <MessageList
                customMessageActions={customMessageActions}
                formatDate={formatMessageTimestamp}
                onlySenderCanEdit
                returnAllReadData
                messageActions={
                  userCanModerateMessages(channel)
                    ? MODERATED_MESSAGE_ACTIONS
                    : undefined
                }
              />
              <div className="py-1 border-t border-slate-100">
                <ScheduledMessagesBanner channel={channel} />
                {canSendInChannel(channel) ? (
                  <MessageInput maxRows={10} />
                ) : (
                  <p className="px-4 py-3 text-center text-sm text-slate-500">
                    {readOnlyNoticeText(channel)}
                  </p>
                )}
              </div>
            </Window>
            <Thread
              additionalMessageListProps={{
                formatDate: formatMessageTimestamp,
                onlySenderCanEdit: true,
                returnAllReadData: true,
              }}
              // Hiding the main composer left the thread's reply box usable on
              // a read-only channel, so a member could write a reply and have
              // the server refuse it. undefined keeps Stream's own default.
              Input={canSendInChannel(channel) ? undefined : ReadOnlyComposerNotice}
            />
            </WithComponents>
          </Channel>
          </>
          )}
        </div>
      </div>
      <CreateChannelModal
        open={showCreateChannel}
        handleClose={() => setShowCreateChannel(false)}
        handleCreateChannel={handleCreateChannel}
        channelName={channelName}
        setChannelName={setChannelName}
        channelDescription={channelDescription}
        setChannelDescription={setChannelDescription}
        selectedMembers={selectedMembers}
        setSelectedMembers={setSelectedMembers}
        handleCreateDM={handleCreateDM}
        modalType={
          props.permissionRole === "company_employee" ? "DM" : modalType
        }
        setModalType={setModalType}
        modalEdit={modalEdit}
        homeStore={props.homeStore}
        chatUsers={chatUsers}
        permissionRole={props.permissionRole}
      />
      <ConversationDetailsModal
        open={Boolean(detailsChannel)}
        onClose={closeDetails}
        channel={detailsChannel}
        currentUserId={client?.userID}
        todayDateStr={todayDateStr}
        storeNames={dailyStoreNames}
        chatUsers={chatUsers}
        GroupAvatar={GroupDirectMessageAvatar}
        onEditChannel={handleEditChannel}
        onAddChannelMembers={handleAddMembersToChannel}
        onAddPeopleToDirectMessage={handleAddPeopleToDirectMessage}
        onRemoveMember={handleRemoveMemberFromChannel}
        onPromoteModerator={handlePromoteToModerator}
        onDemoteModerator={handleDemoteFromModerator}
        onMute={handleMuteChannel}
        onUnmute={handleUnmuteChannel}
        onTogglePin={handleTogglePin}
        onLeave={handleLeaveConversation}
        onDeleteChannel={handleDeleteChannel}
      />
    </ActiveChannelReadReceiptsProvider>
    </ReadReceiptsPolicyContext.Provider>
    </RevealedTimestampsContext.Provider>
    </ScheduledMessagesProvider>
  );
}

export default ChatDisplay;
